import { Modal, Card, Row, Col, message, Tabs, Checkbox, Button, Empty, Tag, Space, Tooltip, Divider } from "antd";
import {
  CreditCardOutlined,
  BankOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BarsOutlined,
} from "@ant-design/icons";
import { ApiService } from "api/api.service";
import { useState, useEffect, useRef } from "react";
import { ApplicationDto, PaymentMethods } from "dtos/application.dto";
import { Gateway } from "dtos/wallet.transaction.dto";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { TbReload } from "react-icons/tb";
import ImportPaynoteButton from "./ImportPaynoteButton";

type Props = {
  open: boolean;
  onClose: () => void;
  appkey: string;
};

type PaymentMethod = NonNullable<ApplicationDto["payment_info"]>[number];

const addMethodOptions = [
  { name: "Paynote x Plaid", img: "/paynote_logo.jpeg", mode: PaymentMethods.PAYNOTE_PLAID },
  { name: "Paynote", img: "/paynote_logo.jpeg", mode: PaymentMethods.PAYNOTE },
  { name: "SeamlessChex Merchant Credit Card", img: "/seamlesschex_logo.jpeg", mode: PaymentMethods.CREDIT_CARD },
  { name: "SeamlessChex eCheck", img: "/seamlesschex_logo.jpeg", mode: PaymentMethods.ECHECK },
] as const;

const getPaymentMethodIcon = (type: string) => {
  if (type === PaymentMethods.CREDIT_CARD) return <CreditCardOutlined style={{ fontSize: 22 }} />;
  if (type === PaymentMethods.ECHECK) return <BankOutlined style={{ fontSize: 22 }} />;
  return <CheckCircleOutlined style={{ fontSize: 22 }} />;
};

const getPaymentMethodLabel = (method: PaymentMethod) => {
  if (method.type === PaymentMethods.CREDIT_CARD) return "Credit Card";
  if (method.type === PaymentMethods.ECHECK) return "eCheck";
  if (method.type === PaymentMethods.PAYNOTE) return "Paynote Bank";
  if (method.type === PaymentMethods.PAYNOTE_PLAID) return "Paynote Bank (via Plaid)";
  return method.type;
};

const getGatewayLabel = (gateway: Gateway) => {
  const labels: Record<Gateway, string> = {
    [Gateway.STRIPE]: "Stripe",
    [Gateway.SEAMLESSCHEX]: "SeamlessChex",
    [Gateway.SEAMLESSCHEX_MERCHANT]: "SeamlessChex Merchant",
    [Gateway.PAYNOTE]: "Paynote",
    [Gateway.MANUALLY_MARKED]: "Manual",
    [Gateway.AUTOMATED_DISTRIBUTION]: "Automated Distribution",
  };
  return labels[gateway] || gateway;
};

const isEcheckOrPaynote = (type: PaymentMethods) =>
  type === PaymentMethods.ECHECK || type === PaymentMethods.PAYNOTE || type === PaymentMethods.PAYNOTE_PLAID;

const MethodSecondaryInfo = ({ method }: { method: PaymentMethod }) => {
  const parts: React.ReactNode[] = [];

  if (method.mask) {
    parts.push(
      <span key="mask" style={{ fontFamily: "monospace", letterSpacing: 1, flexShrink: 0 }}>
        •••• {method.mask}
      </span>,
    );
  }

  if (isEcheckOrPaynote(method.type)) {
    if (method.email) {
      parts.push(
        <span key="email" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {method.email}
        </span>,
      );
    }
    if (method.bank_name) {
      parts.push(
        <span
          key="bank"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <BankOutlined style={{ fontSize: 12, flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{method.bank_name}</span>
        </span>,
      );
    }
  }

  if (parts.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        color: "#555",
        marginTop: 4,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {parts.map((part, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, minWidth: 0 }}>
          {i > 0 && <span style={{ color: "#ccc", flexShrink: 0 }}>•</span>}
          {part}
        </span>
      ))}
    </div>
  );
};

const PaynoteVerificationBadge = ({ verified }: { verified?: boolean }) => {
  if (verified === true) return null;
  return (
    <Tooltip title="This bank account has not been verified yet. Verification may be required before processing payments.">
      <Tag icon={<WarningOutlined />} color="warning" style={{ cursor: "default", fontSize: 11, marginLeft: 0 }}>
        Bank Not Verified
      </Tag>
    </Tooltip>
  );
};

const DraggableMethodCard = ({
  method,
  priority,
  onToggle,
}: {
  method: PaymentMethod;
  priority: number;
  onToggle: (methodId: string, checked: boolean) => void;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: method._id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card
        onClick={() => onToggle(method._id!, false)}
        style={{
          border: "2px solid #1890ff",
          borderRadius: 8,
          transition: "border-color 0.2s, box-shadow 0.2s",
          boxShadow: "0 0 0 3px rgba(24,144,255,0.08)",
          cursor: "pointer",
        }}
        bodyStyle={{ padding: "14px 16px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            {...attributes}
            {...listeners}
            style={{
              cursor: "grab",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              color: "#1890ff",
              flexShrink: 0,
            }}
          >
            <BarsOutlined style={{ fontSize: 16 }} />
          </div>

          <Checkbox
            checked={true}
            onChange={(e) => {
              e.stopPropagation();
              onToggle(method._id!, false);
            }}
            onClick={(e) => e.stopPropagation()}
          />

          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: "#e6f4ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#1890ff",
              flexShrink: 0,
            }}
          >
            {getPaymentMethodIcon(method.type)}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 2 }}>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{getPaymentMethodLabel(method)}</span>
              <Tag color="cyan" style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                Priority {priority + 1}
              </Tag>
              <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
                {getGatewayLabel(method.gateway)}
              </Tag>
              {method.is_active && (
                <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                  Active
                </Tag>
              )}
              {(method.type === PaymentMethods.PAYNOTE || method.type === PaymentMethods.PAYNOTE_PLAID) && (
                <PaynoteVerificationBadge verified={method.paynote_bank_verified} />
              )}
            </div>

            <MethodSecondaryInfo method={method} />
          </div>
        </div>
      </Card>
    </div>
  );
};

const UnselectedMethodCard = ({
  method,
  onToggle,
}: {
  method: PaymentMethod;
  onToggle: (methodId: string, checked: boolean) => void;
}) => {
  return (
    <Card
      onClick={() => method._id && onToggle(method._id, true)}
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: 8,
        transition: "border-color 0.2s, box-shadow 0.2s",
        cursor: "pointer",
      }}
      bodyStyle={{ padding: "14px 16px" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Checkbox
          checked={false}
          onChange={(e) => {
            e.stopPropagation();
            method._id && onToggle(method._id, true);
          }}
          onClick={(e) => e.stopPropagation()}
        />

        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: "#f5f5f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#8c8c8c",
            flexShrink: 0,
          }}
        >
          {getPaymentMethodIcon(method.type)}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, marginBottom: 2 }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{getPaymentMethodLabel(method)}</span>
            <Tag color="blue" style={{ margin: 0, fontSize: 11 }}>
              {getGatewayLabel(method.gateway)}
            </Tag>
            {method.is_active && (
              <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                Active
              </Tag>
            )}
            {method.type === PaymentMethods.PAYNOTE && (
              <PaynoteVerificationBadge verified={method.paynote_bank_verified} />
            )}
          </div>

          <MethodSecondaryInfo method={method} />
        </div>
      </div>
    </Card>
  );
};

export default function PaymentMethodModal({ open, onClose, appkey }: Props) {
  const existingPaymentMethods = ApiService.application.useGetPaymentMethodInfo(appkey);
  const editPaymentMethodInfo = ApiService.application.useEditPaymentMethodInfo();
  const { mutate, isPending } = ApiService.upstream.useStartPaymentDetailsCapture();
  const [activeMode, setActiveMode] = useState<PaymentMethods | null>(null);
  const [selectedMethods, setSelectedMethods] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const paymentMethods = existingPaymentMethods.data?.result || [];

  useEffect(() => {
    if (paymentMethods.length > 0) {
      const initialActive = paymentMethods.filter((pm) => pm.is_active && pm._id).map((pm) => pm._id!);
      setSelectedMethods(initialActive);
    }
  }, [paymentMethods]);

  const handleAddNewMethod = (mode: PaymentMethods) => {
    setActiveMode(mode);
    mutate(
      { appkey, mode },
      {
        onSuccess: () => {
          message.success("Payment details sent to client.");
          setActiveMode(null);
          existingPaymentMethods.refetch();
        },
        onError: () => {
          setActiveMode(null);
        },
      },
    );
  };

  const handleMethodToggle = (methodId: string, checked: boolean) => {
    let newSelection = [...selectedMethods];

    if (checked) {
      if (newSelection.length >= 3) {
        message.warning("You can only have up to 3 active payment methods.");
        return;
      }
      newSelection.push(methodId);

      setTimeout(() => {
        scrollContainerRef.current?.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 0);
    } else {
      newSelection = newSelection.filter((id) => id !== methodId);
      if (newSelection.length === 0) {
        message.warning("At least 1 payment method must be active.");
        return;
      }
    }

    setSelectedMethods(newSelection);
    setHasChanges(true);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedMethods.indexOf(active.id as string);
      const newIndex = selectedMethods.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = arrayMove(selectedMethods, oldIndex, newIndex);
        setSelectedMethods(newOrder);
        setHasChanges(true);
      }
    }
  };

  const handleSaveActiveSelection = () => {
    editPaymentMethodInfo.mutate(
      { appkey, ids: selectedMethods },
      {
        onSuccess: () => {
          message.success("Active payment methods updated successfully.");
          setHasChanges(false);
          existingPaymentMethods.refetch();
        },
      },
    );
  };

  const ExistingMethodsTab = () => {
    if (existingPaymentMethods.isLoading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          Fetching your saved methods. It may take a while as it also syncs the verification status of certain providers
          with your banking information...
        </div>
      );
    }

    if (!paymentMethods || paymentMethods.length === 0) {
      return <Empty description="No payment methods found" style={{ padding: "40px" }} />;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        <div style={{ marginBottom: 12, color: "#666", fontSize: 13 }}>
          Select 1–3 active payment methods&nbsp;
          <Tag style={{ marginLeft: 4 }}>{selectedMethods.length} / 3 selected</Tag>
          {selectedMethods.length > 0 && (
            <span style={{ marginLeft: 8, color: "#999", fontSize: 12 }}>• Drag to reorder priority</span>
          )}
          <Button
            className="ml-4"
            size="small"
            icon={<TbReload />}
            type="primary"
            loading={existingPaymentMethods.isRefetching}
            onClick={() => existingPaymentMethods.refetch()}
          >
            Refetch
          </Button>
        </div>

        <div
          ref={scrollContainerRef}
          style={{
            overflowY: "auto",
            maxHeight: "calc(60vh - 80px)",
            paddingRight: 4,
          }}
        >
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={selectedMethods} strategy={verticalListSortingStrategy}>
              <Row gutter={[12, 12]}>
                {selectedMethods.map((id, priority) => {
                  const method = paymentMethods.find((pm) => pm._id === id);
                  if (!method) return null;
                  return (
                    <Col span={24} key={id}>
                      <DraggableMethodCard method={method} priority={priority} onToggle={handleMethodToggle} />
                    </Col>
                  );
                })}
                {paymentMethods
                  .filter((method) => !selectedMethods.includes(method._id!))
                  .map((method) => (
                    <Col span={24} key={method._id}>
                      <UnselectedMethodCard method={method} onToggle={handleMethodToggle} />
                    </Col>
                  ))}
              </Row>
            </SortableContext>
          </DndContext>
        </div>

        {hasChanges && (
          <div
            style={{
              marginTop: 16,
              paddingTop: 16,
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Space>
              <Button
                onClick={() => {
                  const initialActive = paymentMethods.filter((pm) => pm.is_active && pm._id).map((pm) => pm._id!);
                  setSelectedMethods(initialActive);
                  setHasChanges(false);
                }}
              >
                Discard Changes
              </Button>
              <Button
                type="primary"
                loading={editPaymentMethodInfo.isPending}
                onClick={handleSaveActiveSelection}
                disabled={selectedMethods.length < 1 || selectedMethods.length > 3}
              >
                Save Active Selection
              </Button>
            </Space>
          </div>
        )}
      </div>
    );
  };

  const AddNewMethodTab = () => (
    <div>
      <div style={{ marginBottom: 16, color: "#666", fontSize: 13 }}>
        Select a payment method to send a details capture request to the client
      </div>

      <ImportPaynoteButton ref_id={appkey} refetch={onClose} type={"Application"} />

      <Divider style={{ margin: "20px 0", color: "#999", fontSize: 12 }}>OR</Divider>

      <Row gutter={[12, 12]}>
        {addMethodOptions.map((opt) => (
          <Col span={8} key={opt.mode} style={{ display: "flex" }}>
            <Card
              onClick={() => !isPending && handleAddNewMethod(opt.mode)}
              loading={isPending && activeMode === opt.mode}
              style={{
                textAlign: "center",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                cursor: isPending ? "not-allowed" : "pointer",
                opacity: isPending && activeMode !== opt.mode ? 0.5 : 1,
                transition: "opacity 0.2s",
                borderRadius: 8,
              }}
              bodyStyle={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "8px 12px",
              }}
              cover={
                <div
                  style={{
                    height: 72,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px 12px 0",
                  }}
                >
                  <img
                    src={opt.img}
                    alt={opt.name}
                    style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                  />
                </div>
              }
            >
              <div style={{ fontWeight: 500, fontSize: 13 }}>{opt.name}</div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );

  const tabItems = [
    {
      key: "existing",
      label: `Existing Methods (${paymentMethods.length})`,
      children: <ExistingMethodsTab />,
    },
    {
      key: "add",
      label: "Add New Method",
      children: <AddNewMethodTab />,
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title="Manage Payment Methods"
      width={1000}
      destroyOnHidden
      centered
      styles={{
        body: {
          maxHeight: "calc(80vh - 55px)",
          overflowY: "auto",
          paddingTop: 8,
        },
      }}
    >
      <Tabs items={tabItems} defaultActiveKey="existing" />
    </Modal>
  );
}
